import { useEffect, useRef } from 'react';

/**
 * Shared modal accessibility behavior:
 * - Focuses the modal panel when it opens
 * - Restores focus to the previously focused element when it closes
 * - Closes the modal on Escape
 *
 * Usage:
 *   const panelRef = useModalBehavior({ isOpen, onClose });
 *   <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="my-title">
 */
export default function useModalBehavior({ isOpen, onClose }) {
  const panelRef = useRef(null);
  const previousActiveElementRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    previousActiveElementRef.current = document.activeElement;

    // Focus the panel itself (needs tabIndex={-1} on the element to be focusable)
    if (panelRef.current) {
      panelRef.current.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (onClose) {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      const previouslyFocused = previousActiveElementRef.current;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, onClose]);

  return panelRef;
}
